import { CurrencyPipe } from '@angular/common';
import {
  booleanAttribute,
  Component,
  computed,
  HostBinding,
  input,
  output
} from '@angular/core';

import { UmssIconComponent } from '../icon/icon.component';
import { UmssLinkButtonComponent } from '../link-button/link-button.component';
import { UmssSkeletonComponent } from '../skeleton/skeleton.component';
import {
  resolveSubscriptionTier,
  SubscriptionTierBand
} from '@umss/shared/data-access';

import {
  SubscriptionWidgetScaleMark,
  SubscriptionWidgetTierCaption
} from './subscription-widget.model';

/**
 * Shift-usage summary for the current billing cycle.
 *
 * Renders the two counts that drive subscription billing — shifts already
 * worked and total shifts scheduled — against the tier scale, with the flat
 * monthly price each count currently resolves to.
 *
 * `scheduledShifts` is the cumulative provisional count from Specification v2
 * §5: it includes the worked shifts rather than sitting alongside them, so the
 * two bands are nested and the scheduled price is the forward-looking one.
 *
 * Two presentations, differing only in chrome and layout — the chart itself is
 * identical in both:
 *  - `Compact` — self-contained card with a leading icon and the title inline
 *    with the legend. For the dashboard and the shifts list.
 *  - `Detailed` — no card chrome, title stacked above the legend, and a row of
 *    per-tier price captions beneath the scale. For the subscriptions page,
 *    which supplies its own section framing.
 */
@Component({
  selector: 'umss-subscription-widget',
  standalone: true,
  imports: [
    CurrencyPipe,
    UmssIconComponent,
    UmssLinkButtonComponent,
    UmssSkeletonComponent
  ],
  templateUrl: './subscription-widget.component.html',
  styleUrls: ['./subscription-widget.component.scss']
})
export class UmssSubscriptionWidgetComponent {
  @HostBinding('style.display') display = 'block';

  /** Volume-based bands for the subscription, in any order. */
  tiers = input.required<readonly SubscriptionTierBand[]>();

  /** Shifts worked so far in the current billing cycle. */
  workedShifts = input.required<number>();

  /** Total shifts scheduled for the cycle, inclusive of the worked ones. */
  scheduledShifts = input.required<number>();

  /** Content props */
  /**
   * "Billing period" rather than "month": a cycle is only a calendar month once
   * a facility has been subscribed through a whole one. Activating mid-cycle
   * gives a part-month period, and a heading promising the month is then simply
   * false about the figures under it.
   */
  title = input('Shifts usage this billing period');

  /**
   * The period the figures cover, e.g. `20 Aug – 1 Sep`.
   *
   * For the surfaces that show the widget alone — the dashboard and the shifts
   * page — where nothing else on screen says which window is being counted. The
   * subscriptions pages state it above the widget, so they leave this empty.
   */
  periodLabel = input('');
  viewLabel = input('View');

  /**
   * Replaces the legend figures with an explanation, for a facility that has
   * nothing to report yet.
   *
   * The bands and the track still render, so the reader can see how they would
   * be billed — but zeroed counts and a `$0/mo` price are not shown, since to
   * an unsubscribed facility they read as a bill of nothing rather than as an
   * absence of data.
   */
  emptyMessage = input<string | null>(null);

  /** Variant props */
  variant = input<'Compact' | 'Detailed'>('Compact');

  /** Shows the trailing action that links through to the subscription page. */
  showViewAction = input(true, { transform: booleanAttribute });

  /** State props */
  loading = input(false, { transform: booleanAttribute });

  /** Event outputs */
  viewClick = output<void>();

  /**
   * Visual headroom, in shift-equivalents, given to the open-ended top tier so
   * it has a drawable width.
   *
   * 25 reproduces the Figma track exactly: against a 1108px track, 22 worked
   * shifts measure 195.008px (22/125) and 38 scheduled measure 336.832px
   * (38/125), putting the 100-shift boundary at 80%.
   */
  private static readonly OPEN_TIER_HEADROOM = 25;

  private readonly sortedTiers = computed(() =>
    [...this.tiers()].sort((a, b) => a.lowerBound - b.lowerBound)
  );

  protected readonly workedCount = computed(() =>
    Math.max(0, this.workedShifts())
  );

  /**
   * Scheduled is expected to include worked; clamping here means inconsistent
   * data can never draw the scheduled band shorter than the worked one.
   */
  protected readonly scheduledCount = computed(() =>
    Math.max(this.workedCount(), this.scheduledShifts())
  );

  /** Upper bound of the scale: the top tier ceiling plus open-tier headroom. */
  private readonly scaleMax = computed(() => {
    const tiers = this.sortedTiers();
    // The API omits `upperBound` on the open-ended band rather than sending
    // null, so an identity check against null lets `undefined` through and
    // Math.max turns the whole scale into NaN.
    const ceilings = tiers
      .map((tier) => tier.upperBound)
      .filter((to): to is number => to != null);

    const top = ceilings.length
      ? Math.max(...ceilings)
      : Math.max(0, ...tiers.map((tier) => tier.lowerBound));

    return tiers.some((tier) => tier.upperBound == null)
      ? top + UmssSubscriptionWidgetComponent.OPEN_TIER_HEADROOM
      : top;
  });

  protected readonly workedPercent = computed(() =>
    this.toPercent(this.workedCount())
  );

  protected readonly scheduledPercent = computed(() =>
    this.toPercent(this.scheduledCount())
  );

  protected readonly workedPrice = computed(() =>
    this.priceFor(this.workedCount())
  );

  protected readonly scheduledPrice = computed(() =>
    this.priceFor(this.scheduledCount())
  );

  /**
   * Labelled marks beneath the track: the first tier's floor, then every tier
   * ceiling. The floor is pinned to 0% so it reads as the start of the scale.
   */
  protected readonly scaleMarks = computed<SubscriptionWidgetScaleMark[]>(() => {
    const tiers = this.sortedTiers();
    if (!tiers.length) {
      return [];
    }

    return [
      { value: tiers[0].lowerBound, percent: 0 },
      ...tiers
        .filter(
          (tier): tier is SubscriptionTierBand & { upperBound: number } =>
            tier.upperBound != null
        )
        .map((tier) => ({
          value: tier.upperBound,
          percent: this.toPercent(tier.upperBound)
        }))
    ];
  });

  /** Divider positions along the track — one per tier ceiling. */
  protected readonly boundaryPercents = computed(() =>
    this.scaleMarks()
      .slice(1)
      .map((mark) => mark.percent)
  );

  /**
   * Per-tier price captions for the `Detailed` variant, each sized to the width
   * of the band it labels. Widths come from the same scale as the marks, so the
   * captions stay aligned to the dividers whatever the tier data looks like.
   *
   * Bare price: the band is already named by the scale marks directly above,
   * and `/mo` by both the tier table and the legend. Anything longer will not
   * fit the narrowest band — five shifts is roughly 4% of the track.
   */
  protected readonly tierCaptions = computed<SubscriptionWidgetTierCaption[]>(
    () =>
      this.sortedTiers().map((tier) => ({
        price: tier.price,
        widthPercent:
          (tier.upperBound == null ? 100 : this.toPercent(tier.upperBound)) -
          this.toPercent(tier.lowerBound - 1)
      }))
  );

  private toPercent(shifts: number): number {
    const max = this.scaleMax();
    if (max <= 0) {
      return 0;
    }
    return Math.min(100, Math.max(0, (shifts / max) * 100));
  }

  private priceFor(shifts: number): number {
    return resolveSubscriptionTier(this.sortedTiers(), shifts)?.price ?? 0;
  }
}
